import { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  CardMedia,
  CardActions,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Box,
  Chip,
  IconButton,
  Alert,
  Snackbar,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  ListItemText,
  Checkbox
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  CloudUpload,
  CheckCircle,
  Schedule,
  HourglassEmpty,
  Cancel,
  CalendarToday
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import {
  getAllProjects,
  createProject,
  updateProject,
  deleteProject,
  uploadFile,
  resolveImageUrl,
  getAllServices
} from '../../utils/api';
import AdminLayout from '../../components/AdminLayout';

const ProjectCard = styled(Card)(({ theme }) => ({
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  transition: 'transform 0.2s ease-in-out',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: theme.shadows[4],
  },
}));

const VisuallyHiddenInput = styled('input')({
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: 1,
  overflow: 'hidden',
  position: 'absolute',
  bottom: 0,
  left: 0,
  whiteSpace: 'nowrap',
  width: 1,
});

const ManageProjects = () => {

  // Helper functions for date and status handling
  const formatDate = (dateString) => {
    if (!dateString || dateString === '' || dateString === '0000-00-00' || dateString === null) {
      return null; // Return null for missing dates
    }

    const date = new Date(dateString);
    if (isNaN(date.getTime())) {
      return null; // Return null for invalid dates
    }

    return date.toLocaleDateString();
  };

  const getProjectStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'completed': return 'success';
      case 'in progress':
      case 'in-progress':
      case 'ongoing': return 'warning';
      case 'pending':
      case 'planning':
      case 'upcoming': return 'info';
      case 'cancelled': return 'error';
      default: return 'default';
    }
  };

  const getProjectStatusIcon = (status) => {
    switch (status?.toLowerCase()) {
      case 'completed': return <CheckCircle />;
      case 'in progress':
      case 'in-progress':
      case 'ongoing': return <Schedule />;
      case 'pending':
      case 'planning':
      case 'upcoming': return <HourglassEmpty />;
      case 'cancelled': return <Cancel />;
      default: return <HourglassEmpty />;
    }
  };
  const [projects, setProjects] = useState([]);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingProject, setEditingProject] = useState(null);
  const [primaryImageIndex, setPrimaryImageIndex] = useState(0);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    location: '',
    status: 'ongoing',
    completion_date: '',
    image_url: '',
    service_ids: [], // changed from service_id to service_ids (array)
    images: []
  });
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploadLoading, setUploadLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  useEffect(() => {
    fetchProjects();
    fetchServices();
  }, []);

  const fetchServices = async () => {
    try {
      const response = await getAllServices();
      if (response.data && response.data.success) {
        setServices(response.data.services || []);
      }
    } catch (error) {
      showSnackbar('Failed to load services', 'error');
    }
  };

  const fetchProjects = async () => {
    try {
      setLoading(true);
      const response = await getAllProjects();
      setProjects(response.data.projects || []);
    } catch (error) {
      showSnackbar('Failed to load projects', 'error');
    } finally {
      setLoading(false);
    }
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleOpenDialog = (project = null) => {
    if (project) {
      setEditingProject(project);
      setFormData({
        title: project.title || '',
        description: project.description || '',
        location: project.location || '',
        status: project.status || 'ongoing',
        completion_date: project.completion_date || '',
        image_url: project.image_url || '',
        service_ids: project.services ? project.services.map(s => s.id) : [],
        images: project.images
          ? project.images.map(img => img.image_url ? img.image_url : img)
          : [], // handle both array of objects and array of strings
      });
      setPrimaryImageIndex(0); // Default to first image for now
    } else {
      setEditingProject(null);
      setFormData({
        title: '',
        description: '',
        location: '',
        status: 'ongoing',
        completion_date: '',
        image_url: '',
        service_ids: [],
        images: []
      });
      setPrimaryImageIndex(0); // Default to first image for now
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingProject(null);
    setFormData({
      title: '',
      description: '',
      location: '',
      status: 'ongoing',
      completion_date: '',
      image_url: '',
      service_ids: [],
      images: []
    });
    setSelectedFiles([]);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    setSelectedFiles(files);
  };

  const handleUploadImages = async () => {
    if (selectedFiles.length === 0) return [];

    try {
      setUploadLoading(true);
      const uploadPromises = selectedFiles.map(file => uploadFile(file));
      const responses = await Promise.all(uploadPromises);

      const uploadedUrls = responses
        .filter(response => response.data.success)
        .map(response => response.data.url);

      if (uploadedUrls.length !== selectedFiles.length) {
        showSnackbar('Some images failed to upload', 'warning');
      }

      // After upload, append new images and keep primaryImageIndex as is
      setFormData(prev => ({
        ...prev,
        images: [...prev.images, ...uploadedUrls]
      }));

      return uploadedUrls;
    } catch (error) {
      showSnackbar('Failed to upload images', 'error');
      return [];
    } finally {
      setUploadLoading(false);
    }
  };

  const handleRemoveImage = (index) => {
    setFormData(prev => {
      const newImages = prev.images.filter((_, i) => i !== index);
      // If primary image was removed, reset to 0
      let newPrimary = primaryImageIndex;
      if (index === primaryImageIndex) newPrimary = 0;
      else if (index < primaryImageIndex) newPrimary = primaryImageIndex - 1;
      setPrimaryImageIndex(newPrimary);
      return { ...prev, images: newImages };
    });
  };

  const handleSubmit = async () => {
    if (!formData.title || !formData.description) {
      showSnackbar('Please fill in all required fields', 'error');
      return;
    }

    // Validate completion date for completed projects
    if (formData.status?.toLowerCase() === 'completed' && !formData.completion_date) {
      showSnackbar('Completion date is required for completed projects', 'error');
      return;
    }

    try {
      let imageUrls = formData.images || [];

      // Upload new images if selected
      if (selectedFiles.length > 0) {
        const newImageUrls = await handleUploadImages();
        if (newImageUrls.length === 0) return; // Upload failed
        imageUrls = [...imageUrls, ...newImageUrls];
      }

      // Move the selected primary image to the first position
      if (imageUrls.length > 1 && primaryImageIndex < imageUrls.length) {
        const primary = imageUrls[primaryImageIndex];
        imageUrls = [primary, ...imageUrls.filter((_, i) => i !== primaryImageIndex)];
      }

      const projectData = {
        ...formData,
        // Convert empty completion_date to null to avoid database issues
        completion_date: formData.completion_date === '' ? null : formData.completion_date,
        // Convert service_id to integer or null
        service_ids: formData.service_ids,
        images: imageUrls
      };

      if (editingProject) {
        await updateProject(editingProject.id, projectData);
        showSnackbar('Project updated successfully');
      } else {
        await createProject(projectData);
        showSnackbar('Project created successfully');
      }

      handleCloseDialog();
      fetchProjects();
    } catch (error) {
      showSnackbar('Failed to save project', 'error');
    }
  };

  const handleDelete = async (projectId, projectTitle) => {
    if (window.confirm(`Are you sure you want to delete "${projectTitle}"?`)) {
      try {
        await deleteProject(projectId);
        showSnackbar('Project deleted successfully');
        fetchProjects();
      } catch (error) {
        showSnackbar('Failed to delete project', 'error');
      }
    }
  };

  return (
    <AdminLayout title="Manage Projects">
      <Container maxWidth="lg">
        {/* Header Actions */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
          <Grid item xs={12} md={6}>
            <Typography variant="h4" sx={{
              fontWeight: 600,
              fontSize: { xs: '1.6rem', sm: '1.8rem', md: '2rem' }
            }}>
              Projects Management
            </Typography>
            <Typography variant="body3" color="text.secondary">
              Total Projects: {projects.length}
            </Typography>
          </Grid>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => handleOpenDialog()}
              sx={{
                fontSize: { xs: '0.5rem', sm: '0.9rem', md: '1rem' }
              }}
            >
              Add Project
            </Button>
          </Box>
        </Box>

        {/* Loading State */}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress size={60} />
          </Box>
        ) : (
          <>
            {/* Projects Grid */}
            {projects.length === 0 ? (
              <Card sx={{ p: 6, textAlign: 'center' }}>
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  No projects found
                </Typography>
                <Typography variant="body2" color="text.secondary" paragraph>
                  Start by adding your first project to showcase your work.
                </Typography>
                <Button
                  variant="contained"
                  startIcon={<Add />}
                  onClick={() => handleOpenDialog()}
                >
                  Add First Project
                </Button>
              </Card>
            ) : (
              <Grid container spacing={3}>
                {projects.map((project) => (
                  <Grid item xs={12} sm={6} md={4} key={project.id}>
                    <ProjectCard>
                      <CardMedia
                        component="img"
                        height="200"
                        image={resolveImageUrl(
                          project.images && project.images.length > 0
                            ? (project.images[0].image_url || project.images[0])
                            : project.image_url
                        )}
                        alt={project.title}
                        sx={{ objectFit: 'cover' }}
                      />
                      <CardContent sx={{ flexGrow: 1 }}>
                        <Typography variant="h6" gutterBottom>
                          {project.title}
                        </Typography>

                        {/* Status and Completion Date - moved above description */}
                        <Box sx={{ mb: 2 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                            {getProjectStatusIcon(project.status)}
                            <Chip
                              label={project.status || 'Unknown'}
                              color={getProjectStatusColor(project.status)}
                              size="small"
                              variant="outlined"
                            />
                          </Box>
                          <Box sx={{ mb: 1 }}>
                            {project.status?.toLowerCase() === 'completed' && formatDate(project.completion_date) ? (
                              <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <CalendarToday sx={{ fontSize: 14 }} />
                                Completed: {formatDate(project.completion_date)}
                              </Typography>
                            ) : project.status?.toLowerCase() === 'completed' ? (
                              <Typography variant="caption" color="text.secondary">
                                Completion Date : N/A
                              </Typography>
                            ) : (
                              <Typography variant="caption" color="text.secondary">
                                {project.status?.toLowerCase() === 'ongoing' ? 'Project in progress' :
                                  project.status?.toLowerCase() === 'upcoming' ? 'Project upcoming' :
                                    'Status: ' + (project.status || 'Unknown')}
                              </Typography>
                            )}
                          </Box>
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            {project.category && (
                              <Chip
                                label={project.category}
                                variant="outlined"
                                size="small"
                              />
                            )}
                            {project.service_title && (
                              <Chip
                                label={`Service: ${project.service_title}`}
                                variant="outlined"
                                size="small"
                                color="primary"
                              />
                            )}
                          </Box>
                        </Box>

                        <Typography
                          variant="body2"
                          color="text.secondary"
                          paragraph
                          sx={{
                            display: '-webkit-box',
                            WebkitLineClamp: 3,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                          }}
                        >
                          {project.description}
                        </Typography>
                      </CardContent>
                      <CardActions>
                        <Button
                          size="small"
                          startIcon={<Edit />}
                          onClick={() => handleOpenDialog(project)}
                        >
                          Edit
                        </Button>
                        <Button
                          size="small"
                          color="error"
                          startIcon={<Delete />}
                          onClick={() => handleDelete(project.id, project.title)}
                        >
                          Delete
                        </Button>
                      </CardActions>
                    </ProjectCard>
                  </Grid>
                ))}
              </Grid>
            )}
          </>
        )}

        {/* Project Dialog */}
        <Dialog
          open={openDialog}
          onClose={handleCloseDialog}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            {editingProject ? 'Edit Project' : 'Add New Project'}
          </DialogTitle>
          <DialogContent>
            <Grid container spacing={3} sx={{ mt: 1 }}>
              <Grid item xs={12}>
                <TextField
                  label="Project Title"
                  name="title"
                  value={formData.title}
                  onChange={handleInputChange}
                  fullWidth
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Description"
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  fullWidth
                  multiline
                  rows={4}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Location"
                  name="location"
                  value={formData.location}
                  onChange={handleInputChange}
                  fullWidth
                  placeholder="e.g., New York, NY"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel id="services-label">Services</InputLabel>
                  <Select
                    labelId="services-label"
                    multiple
                    value={formData.service_ids}
                    onChange={e => setFormData({ ...formData, service_ids: e.target.value })}
                    renderValue={selected =>
                      services
                        .filter(s => selected.includes(s.id))
                        .map(s => s.title)
                        .join(', ')
                    }
                  >
                    {services.map(service => (
                      <MenuItem key={service.id} value={service.id}>
                        <Checkbox checked={formData.service_ids.indexOf(service.id) > -1} />
                        <ListItemText primary={service.title} />
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Status</InputLabel>
                  <Select
                    name="status"
                    value={formData.status}
                    onChange={handleInputChange}
                    label="Status"
                  >
                    <MenuItem value="upcoming">Upcoming</MenuItem>
                    <MenuItem value="ongoing">Ongoing</MenuItem>
                    <MenuItem value="completed">Completed</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Completion Date"
                  name="completion_date"
                  type="date"
                  value={formData.completion_date}
                  onChange={handleInputChange}
                  fullWidth
                  required={formData.status?.toLowerCase() === 'completed'}
                  InputLabelProps={{ shrink: true }}
                  helperText={
                    formData.status?.toLowerCase() === 'completed'
                      ? "Required for completed projects"
                      : "Leave empty if project is not completed yet"
                  }
                />
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2" gutterBottom>
                  Project Images
                </Typography>
                <Button
                  component="label"
                  variant="outlined"
                  startIcon={<CloudUpload />}
                  fullWidth
                  disabled={uploadLoading}
                  sx={{ mb: 2 }}
                >
                  {selectedFiles.length > 0
                    ? `Selected ${selectedFiles.length} image(s)`
                    : 'Upload Project Images'}
                  <VisuallyHiddenInput
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={handleFileChange}
                  />
                </Button>

                {/* Display existing images */}
                {selectedFiles.length > 0 && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      New Images to Upload:
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {selectedFiles.map((file, index) => (
                        <Box
                          key={index}
                          sx={{
                            width: 80,
                            height: 80,
                            border: index === primaryImageIndex ? '2px solid #1976d2' : '1px solid #ddd',
                            borderRadius: 1,
                            overflow: 'hidden',
                            position: 'relative'
                          }}
                        >
                          <img
                            src={URL.createObjectURL(file)}
                            alt={`Preview ${index + 1}`}
                            style={{
                              width: '100%',
                              height: '100%',
                              objectFit: 'cover'
                            }}
                          />
                          {index === primaryImageIndex && (
                            <Chip
                              label="Primary"
                              size="small"
                              color="primary"
                              sx={{
                                position: 'absolute',
                                bottom: 2,
                                left: 2,
                                fontSize: '0.6rem',
                                height: 16
                              }}
                            />
                          )}
                          {index !== primaryImageIndex && (
                            <Button
                              size="small"
                              variant="contained"
                              color="primary"
                              sx={{
                                position: 'absolute',
                                bottom: 2,
                                right: 2,
                                fontSize: '0.6rem',
                                minWidth: 0,
                                px: 0.5,
                                py: 0,
                                height: 18
                              }}
                              onClick={() => setPrimaryImageIndex(index)}
                            >
                              Set as Primary
                            </Button>
                          )}
                        </Box>
                      ))}
                    </Box>
                  </Box>
                )}

                {/* Display existing images (for edit mode) */}
                {formData.images && formData.images.length > 0 && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Current Images ({formData.images.length}):
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {formData.images.map((imageUrl, index) => (
                        <Box
                          key={index}
                          sx={{
                            position: 'relative',
                            width: 80,
                            height: 80,
                            border: selectedFiles.length === 0 && index === primaryImageIndex ? '2px solid #1976d2' : '1px solid #ddd',
                            borderRadius: 1,
                            overflow: 'hidden'
                          }}
                        >
                          <img
                            src={resolveImageUrl(imageUrl)}
                            alt={`Project image ${index + 1}`}
                            style={{
                              width: '100%',
                              height: '100%',
                              objectFit: 'cover'
                            }}
                          />
                          {selectedFiles.length === 0 && index === primaryImageIndex && (
                            <Chip
                              label="Primary"
                              size="small"
                              color="primary"
                              sx={{
                                position: 'absolute',
                                bottom: 2,
                                left: 2,
                                fontSize: '0.6rem',
                                height: 16
                              }}
                            />
                          )}
                          <IconButton
                            size="small"
                            color="error"
                            sx={{
                              position: 'absolute',
                              top: 0,
                              right: 0,
                              bgcolor: 'rgba(255,255,255,0.8)',
                              '&:hover': { bgcolor: 'rgba(255,255,255,0.9)' }
                            }}
                            onClick={() => handleRemoveImage(index)}
                          >
                            <Delete sx={{ fontSize: 14 }} />
                          </IconButton>
                          {selectedFiles.length === 0 && index !== primaryImageIndex && (
                            <Button
                              size="small"
                              variant="contained"
                              color="primary"
                              sx={{
                                position: 'absolute',
                                bottom: 2,
                                right: 2,
                                fontSize: '0.6rem',
                                minWidth: 0,
                                px: 0.5,
                                py: 0,
                                height: 18
                              }}
                              onClick={() => setPrimaryImageIndex(index)}
                            >
                              Set as Primary
                            </Button>
                          )}
                        </Box>
                      ))}
                    </Box>
                  </Box>
                )}

                {uploadLoading && (
                  <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                    <CircularProgress size={20} sx={{ mr: 1 }} />
                    <Typography variant="body2">Uploading images...</Typography>
                  </Box>
                )}

                <Typography variant="caption" color="text.secondary">
                  * The Primary image will be used as the main image on cards.
                </Typography>
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog}>Cancel</Button>
            <Button
              onClick={handleSubmit}
              variant="contained"
              disabled={uploadLoading}
            >
              {editingProject ? 'Update' : 'Create'} Project
            </Button>
          </DialogActions>
        </Dialog>

        {/* Snackbar */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert
            onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
            severity={snackbar.severity}
            sx={{ width: '100%' }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Container>
    </AdminLayout>
  );
};

export default ManageProjects;